/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import { Col, Collapse, Empty, Input, Row, Spin, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import JsonViewer from './JsonViewer';

// Misc imports
import { isJson } from '../../utils/is/isJson';
import { isArray } from '../../utils/is/isArray';

const VariablesList = ({ variables, isLoading, maxHeight, searchEnabled = true }) => {

  const [searchValue, setSearchValue] = useState('');

  const filteredVariables = useMemo(() => {

    if(!variables || variables?.length === 0) {
      return [];
    }
    if (!searchValue) {
      return variables;
    }

    const lowerCaseQuery = searchValue.toLowerCase();

    return variables.filter(variable => {
      const nameMatch = variable.name.toLowerCase().includes(lowerCaseQuery);

      const valueAsString = (typeof variable.value === 'string')
        ? variable.value
        : JSON.stringify(variable.value);

      const valueMatch = valueAsString.toLowerCase().includes(lowerCaseQuery);

      return nameMatch || valueMatch;
    });
  }, [variables, searchValue]);

  const renderLoadingIndicator = () => {
    return (
      <Row>
        <Col xs={24} className="d-flex align-items-center justify-content-center">
          <Spin spinning className="mt-3" />
        </Col>
      </Row>
    );
  };

  const renderVariablesList = () => {

    if(filteredVariables?.length === 0) {
      return <Empty />;
    }

    return (
      <Fragment>
        {
          filteredVariables?.map(variable => {
            const isValueJson = isJson(variable.value);
            if(isValueJson) {
              const valueInJson = typeof variable.value === 'string' ? JSON.parse(variable.value) : variable.value;
              return (
                <Row className="bg-layout-gray mb-3 border-radius-base" key={variable.name}>
                  <Col xs={24} className="p-2">
                    <Collapse ghost >
                      <Collapse.Panel
                        header={
                          <Row>
                            <Col xs={24} className="fw-500">
                              <Typography.Text>
                                {variable.name}
                              </Typography.Text>
                            </Col>
                            <Col xs={24} className="text-muted">
                              {isArray(variable?.value) ? t('Array') : t('JSON')}
                            </Col>
                          </Row>
                        }
                      >
                        <Row>
                          <Col xs={24} className="p-4">
                            <JsonViewer jsonObject={valueInJson}/>
                          </Col>
                        </Row>
                      </Collapse.Panel>
                    </Collapse>
                  </Col>
                </Row>
              );
            }

            return (
              <Row className="bg-layout-gray mb-3 border-radius-base" key={variable.name}>
                <Col xs={24} className="p-2">
                  <Row className="px-6">
                    <Col xs={24} className="fw-500">
                      <Typography.Text>
                        {variable.name}
                      </Typography.Text>
                    </Col>
                    <Col xs={24} className="text-muted">
                      <Typography.Text>
                        {typeof variable.value === 'string'
                          ? variable.value
                          : JSON.stringify(variable.value)}
                      </Typography.Text>
                    </Col>
                  </Row>
                </Col>
              </Row>
            );
          })
        }
      </Fragment>
    );
  };

  const rowProps = useMemo(() => {
    if (maxHeight) {
      return {
        className: 'overflow-y-auto scrollbar-custom px-2',
        style: {
          maxHeight: maxHeight
        }
      };
    }

    return {};
  }, [maxHeight]);

  return (
    <Row {...rowProps}>
      <Col xs={24}>
        <Input
          hidden={!searchEnabled}
          size="large"
          placeholder="Search by name or value"
          prefix={
            <SearchOutlined
              className="text-secondary"
            />
          }
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="mb-3 mt-2"
          allowClear
        />
        {isLoading ? renderLoadingIndicator() : renderVariablesList()}
      </Col>
    </Row>

  );
};

VariablesList.propTypes = {
  variables: PropTypes.array,
  isLoading: PropTypes.bool,
  maxHeight: PropTypes.number,
  searchEnabled: PropTypes.bool
};

export default VariablesList;
